import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../../software/api";
import { toast } from "sonner";
import { Droplet, Mail, MessageSquare, BellOff, Send, ArrowRight, AlertTriangle } from "lucide-react";

const pretty = (iso) => (iso ? iso.split("-").reverse().join("/") : "—");
const daysOverdue = (iso) => {
  if (!iso) return 0;
  const d = new Date(iso + "T00:00:00"), t = new Date(); t.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((t - d) / 86400000));
};

export default function ReminderWorklist({ onOpenClient }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(new Set());
  const [chan, setChan] = useState("all"); // all | email | sms
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await api("GET", "/water-tests/overdue")); } catch { setRows([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => rows.filter((r) =>
    chan === "all" ? true : chan === "email" ? r.notify_email : r.notify_sms), [rows, chan]);

  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allShownSelected = filtered.length > 0 && filtered.every((r) => sel.has(r.client_id));
  const toggleAll = () => setSel((s) => {
    const n = new Set(s);
    if (allShownSelected) filtered.forEach((r) => n.delete(r.client_id));
    else filtered.forEach((r) => n.add(r.client_id));
    return n;
  });

  const send = async (ids) => {
    if (!ids.length) { toast.info("Tick at least one client first."); return; }
    setBusy(true);
    try {
      const res = await api("POST", "/water-tests/remind", { client_ids: ids });
      const keyMsg = (!res.keys.email || !res.keys.sms)
        ? ` (${res.queued} queued — will send automatically once your Resend/Twilio keys are added)` : "";
      toast.success(`Reminders: ${res.sent} sent, ${res.queued} queued, ${res.skipped} skipped${keyMsg}`);
      setSel(new Set());
    } catch (e) { toast.error(e.message || "Could not send reminders"); }
    setBusy(false);
  };

  return (
    <div data-testid="reminder-worklist">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h1 className="font-display text-3xl font-black text-slate-900 leading-tight flex items-center gap-2">
            <Droplet className="h-7 w-7 text-sky-500" /> Water Tests Due
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            <span className="font-semibold text-rose-600" data-testid="overdue-count">{rows.length}</span> client{rows.length === 1 ? "" : "s"} overdue for a water test · reminders respect each client's notify preference
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => send([...sel])} disabled={busy || sel.size === 0} data-testid="send-selected-btn"
            className="inline-flex items-center gap-2 rounded-full bg-sky-600 hover:bg-sky-700 disabled:opacity-40 text-white text-sm font-bold px-5 py-2.5 transition-colors">
            <Send className="h-4 w-4" /> Send to selected ({sel.size})
          </button>
          <button onClick={() => send(filtered.map((r) => r.client_id))} disabled={busy || filtered.length === 0} data-testid="send-all-btn"
            className="inline-flex items-center gap-2 rounded-full border-2 border-sky-300 text-sky-700 hover:bg-sky-50 disabled:opacity-40 text-sm font-bold px-5 py-2.5 transition-colors">
            Send to all shown ({filtered.length})
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 mb-4 flex items-start gap-2 text-[13px] text-amber-800" data-testid="keys-note">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <span>Live sending switches on once your <b>Resend</b> (email) and <b>Twilio</b> (SMS) keys are added. Until then, reminders are <b>queued</b> and logged — nothing is lost. Seasonal cadence: fortnightly Nov–Mar, monthly Apr–Oct.</span>
      </div>

      <div className="flex items-center gap-2 mb-3">
        {[["all", "All channels"], ["email", "Email"], ["sms", "SMS"]].map(([k, lab]) => (
          <button key={k} onClick={() => setChan(k)} data-testid={`worklist-filter-${k}`}
            className={`px-4 py-1.5 rounded-full text-sm font-bold border-2 transition-colors ${chan === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-500 hover:border-slate-400"}`}>{lab}</button>
        ))}
        <label className="ml-auto flex items-center gap-2 text-sm font-semibold text-slate-600">
          <input type="checkbox" checked={allShownSelected} onChange={toggleAll} data-testid="worklist-select-all" /> Select all shown
        </label>
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-400" data-testid="worklist-loading">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-emerald-200 p-10 text-center text-emerald-700 font-display font-black text-lg" data-testid="worklist-empty">
          Nothing overdue — every pool is up to date. 🎉
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 max-h-[calc(100vh-320px)] overflow-y-auto" data-testid="worklist-list">
          {filtered.map((r) => (
            <div key={r.client_id} data-testid="worklist-row" className={`flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 ${sel.has(r.client_id) ? "bg-sky-50/60" : ""}`}>
              <input type="checkbox" checked={sel.has(r.client_id)} onChange={() => toggle(r.client_id)} data-testid={`worklist-check-${r.client_id}`} className="h-4 w-4 shrink-0" />
              <div className="min-w-0 flex-1">
                <button onClick={() => onOpenClient && onOpenClient(r.client_id)} data-testid={`worklist-open-${r.client_id}`}
                  className="text-left font-bold text-slate-800 hover:text-sky-700 truncate block">{r.name} <span className="font-normal text-slate-400">· {r.suburb}</span></button>
                <div className="text-[12px] text-slate-500">Last {pretty(r.last_test_date)} · Due {pretty(r.next_test_date)}</div>
              </div>
              <span className="shrink-0 px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[11px] font-black" data-testid="worklist-days">{daysOverdue(r.next_test_date)}d overdue</span>
              <div className="shrink-0 flex items-center gap-1.5 w-16 justify-end">
                {r.notify_email && <Mail className="h-4 w-4 text-sky-600" data-testid="worklist-ch-email" />}
                {r.notify_sms && <MessageSquare className="h-4 w-4 text-emerald-600" data-testid="worklist-ch-sms" />}
                {!r.notify_email && !r.notify_sms && <BellOff className="h-4 w-4 text-slate-300" title="No contact channel" />}
              </div>
              <button onClick={() => send([r.client_id])} disabled={busy} data-testid={`worklist-send-${r.client_id}`}
                className="shrink-0 inline-flex items-center gap-1 rounded-full bg-sky-600 hover:bg-sky-700 disabled:opacity-40 text-white text-[12px] font-bold px-3 py-1.5">
                Remind <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
